<template>
  <div class="card my-3">
    <div>
      <div class="p-3">
        <h3>{{ $t('billing.budget.transactions.title') }}</h3>
      </div>
      <Tabs v-model="currentTab" :tabs="orderTabs" class="nav-bordered bg-white px-3" />
    </div>
    <div class="table-responsive">
      <table class="table table-vcenter" style="table-layout: fixed">
        <thead>
          <tr>
            <th class="icon-col"></th>
            <th>{{ $t('amount') }}</th>
            <th>{{ $t('description') }}</th>
            <th class="text-end pe-3">{{ $t('settings.company.orders.created_at') }}</th>
          </tr>
        </thead>
        <RemoteListView
          :endpoint="props.endpoint || '/v1/account/active-budget/transactions'"
          :search-query="data.searchQuery"
          :order="data.sortKey"
          :order-dir="data.sortDir"
          :page-size="100"
          :parameters="data.requestParameters"
          :transformData="groupTransactionsByCreated"
          element-type="tbody"
          :key="currentTab + (props.endpoint || '')">
          <template #collection="{ collection }">
            <tr v-for="item in collection" :key="item.id">
              <th class="date-separator" v-if="item.separator" colspan="4">{{ item.separator }}</th>
              <template v-else>
                <td class="icon-col">
                  <IconCircleArrowUp stroke-width="1.5" v-if="item.type === 'deposit'" class="text-success ms-3" />
                  <IconCircleArrowDown stroke-width="1.5" v-if="item.type === 'withdraw'" class="text-danger ms-3" />
                </td>
                <td class="text-nowrap">
                  <p class="m-0">
                    <span>{{ formatCurrency(item.amount, item.currency) }}</span>
                    <span v-if="item.type === 'deposit'" class="ms-2 status status-green">{{ $t('deposit') }}</span>
                    <span v-else-if="item.type === 'withdraw'" class="ms-2 status status-azure">{{
                      $t('withdraw')
                    }}</span>
                  </p>
                </td>
                <td class="text-nowrap text-truncate">
                  <span v-tooltip="item.description">{{ item.description }}</span>
                </td>
                <td class="text-end text-nowrap pe-3">
                  <div class="d-flex gap-1 flex-column">
                    <p class="m-0" v-tooltip="`${formatDateTime(item.created_at)}`">
                      {{
                        formatDistanceToNow(new Date(item.created_at || ''), {
                          addSuffix: true,
                          locale: currentLanguage === 'en_US' ? enUS : de,
                        })
                      }}
                    </p>
                  </div>
                </td>
              </template>
            </tr>
          </template>
          <template #footer="{ page, changePage, refresh, startRecordIndex, endRecordIndex, filterCount, pageCount }">
            <tr>
              <td colspan="4">
                <div class="d-flex align-items-center justify-content-between ms-3">
                  <p class="m-0 text-muted">
                    <small>
                      {{ startRecordIndex }} - {{ endRecordIndex }} {{ $t('of') }} <strong>{{ filterCount }}</strong>
                      {{ $t('entries') }}
                    </small>
                  </p>
                  <LiPagination
                    :modelValue="page"
                    @update:modelValue="changePage"
                    :page-count="pageCount"
                    class="m-0 ms-auto"
                    @change="refresh"
                    :labels="{ prev: $t('orders.pagination.prev'), next: $t('orders.pagination.next') }" />
                </div>
              </td>
            </tr>
          </template>
          <template #empty>
            <tr>
              <td colspan="4">
                <div class="empty">
                  <div class="empty-icon">
                    <IconMoodEmpty :size="24" :stroke-width="1" />
                  </div>
                  <p class="empty-title">{{ $t('billing.budget.transactions.empty') }}</p>
                </div>
              </td>
            </tr>
          </template>
          <template #loading>
            <tr>
              <td colspan="4">
                <div class="d-flex w-full justify-content-center align-items-center">
                  <span class="spinner-border" />
                </div>
              </td>
            </tr>
          </template>
        </RemoteListView>
      </table>
    </div>
  </div>
</template>
<script setup lang="ts">
import translator, { currentLanguage } from '@/locale/translator';
import { reactive, ref, watch } from 'vue';
import RemoteListView from '../RemoteListView.vue';
import { differenceInMonths, differenceInYears, formatDistanceToNow, parseISO } from 'date-fns';
import { enUS, de } from 'date-fns/locale';
import { Pagination as LiPagination } from '@prospective/lithium';
import { IconMoodEmpty, IconCircleArrowUp, IconCircleArrowDown } from '@tabler/icons-vue';
import { formatDateTime } from '@/filters/dateFormat';
import { Tabs } from '@prospective/lithium'; // Added LoadingButton back
import { formatCurrency } from '@/locale/format';
import type { IActiveBudgetTransaction } from '@/types/budget';

const orderTabs = [
  {
    label: translator.get('billing.budget.transactions.everything'),
    value: 'everything',
    isLoading: false,
    badge: undefined,
  },
  {
    label: translator.get('billing.budget.transactions.withdrawals'),
    value: 'withdraw',
    isLoading: false,
    badge: undefined,
  },
  {
    label: translator.get('billing.budget.transactions.deposits'),
    value: 'deposit',
    isLoading: false,
    badge: undefined,
  },
];

const currentTab = ref('everything');
const props = defineProps<{
  companyName?: string;
  endpoint?: string;
}>();
const data = reactive({
  sortKey: 'created_at',
  sortDir: 'desc',
  searchQuery: '',
  requestParameters: {
    dql: '',
  },
});

function groupTransactionsByCreated(transactions: IActiveBudgetTransaction[]) {
  const now = new Date();
  const grouped: any[] = [];
  const insertedSeparators = new Set();

  const getSeparator = (date: Date) => {
    const monthsDiff = differenceInMonths(now, date);

    if (monthsDiff < 1) return;
    if (monthsDiff < 2) return translator.get('time.last_month');
    if (monthsDiff <= 5) return translator.get('time.more_than_two');
    if (monthsDiff <= 11) return translator.get('time.more_than_six');

    const yearsDiff = differenceInYears(now, date);
    if (yearsDiff < 1) return translator.get('time.more_than_year');

    return date.getFullYear().toString();
  };

  for (const trans of transactions) {
    const transDate = parseISO(trans.created_at || '1999-01-01');
    const separator = getSeparator(transDate);

    if (separator && !insertedSeparators.has(separator)) {
      grouped.push({ separator });
      insertedSeparators.add(separator);
    }

    grouped.push(trans);
  }

  return grouped;
}

/* ---------------------------------- WATCH --------------------------------- */
watch(currentTab, (newTab) => {
  data.requestParameters.dql = newTab === 'everything' ? '' : `type=${newTab}`;
});
</script>
<style scoped>
tr > th.date-separator {
  color: black !important;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 6px 12px 6px 32px;
}

tr > th {
  color: black !important;
  font-weight: 700;
}

.icon-col {
  width: 50px;
}
</style>
